import React, { useRef, useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import { useHistory } from "react-router-dom";
import gsap from "gsap";

const ViewAllButton = ({ label = "View All", to = "#", onClick, sx = {} }) => {
  const history = useHistory();
  const theme = useTheme();
  const buttonRef = useRef(null);
  const arrowRef = useRef(null);
  const hoverTl = useRef(null);
  const arrowTl = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      hoverTl.current = gsap
        .timeline({ paused: true })
        .to(
          buttonRef.current,
          {
            scale: 1.05,
            backgroundColor: theme.custom.colors.alpha.white[90],
            boxShadow: `0 10px 30px ${theme.custom.colors.alpha.black[10]}`,
            duration: 0.35,
            ease: "back.out(2)",
          },
          0
        );

      arrowTl.current = gsap
        .timeline({ paused: true })
        .to(arrowRef.current, {
          x: 20,
          opacity: 0,
          duration: 0.2,
          ease: "power2.in",
        })
        .set(arrowRef.current, { x: -20 })
        .to(arrowRef.current, {
          x: 0,
          opacity: 1,
          duration: 0.25,
          ease: "back.out(1.5)",
        });
    });

    return () => ctx.revert();
  }, [theme]);

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
      if (e.defaultPrevented) return;
    }
    if (to && to !== "#") {
      e.preventDefault();
      history.push(to);
    }
  };

  return (
    <Box
      ref={buttonRef}
      component="a"
      href={to}
      onClick={handleClick}
      onMouseEnter={() => {
        hoverTl.current?.play();
        arrowTl.current?.restart();
      }}
      onMouseLeave={() => {
        hoverTl.current?.reverse();
      }}
      className="view-all-link"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        color: "custom.colors.home.navyDark",
        textDecoration: "none !important",
        borderBottom: "none !important",
        fontSize: { xs: "12px", md: "13px" },
        fontWeight: 600,
        whiteSpace: "nowrap",
        padding: "8px 18px",
        borderRadius: "24px",
        background: theme.custom.colors.alpha.white[40],
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${theme.custom.colors.alpha.white[50]}`,
        boxShadow: `0 4px 15px ${theme.custom.colors.alpha.black[4]}`,
        cursor: "pointer",
        "&:hover, &:focus, &:active": {
          textDecoration: "none !important",
          borderBottom: "none !important",
        },
        ...sx,
      }}
    >
      {label}
      <Box 
        ref={arrowRef} 
        component="span" 
        className="link-arrow"
        sx={{
          display: "inline-block",
          fontSize: "15px",
          fontWeight: 800,
          lineHeight: 1,
          textDecoration: "none !important",
          borderBottom: "none !important",
        }}
      >
        ➔
      </Box>
    </Box>
  );
};

export default ViewAllButton;
